import { ReactNode, useMemo } from 'react';
import { Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import {
  SortAscendingOutlined,
  SortDescendingOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  VerticalAlignTopOutlined,
  VerticalAlignBottomOutlined,
  EyeInvisibleOutlined,
  ColumnWidthOutlined,
  EllipsisOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';

import type { ITableColumnMenuOptions } from '../utils/types';
import ColumnResizePopover from './ColumnResizePopover';
import { useToggleVisibility } from '../../../hooks/useToggleVisibility';
import { DEFAULT_COLUMN_WIDTH } from '../utils/constants';

export interface IProps {
  title: ReactNode;
  columnWidth?: number;
  menuOptions?: ITableColumnMenuOptions;
  onSortAsc?: () => void;
  onSortDesc?: () => void;
  onMoveLeft?: () => void;
  onMoveRight?: () => void;
  onMoveFirst?: () => void;
  onMoveLast?: () => void;
  onHide?: () => void;
  onResize?: (width: number) => void;
  onStartResize?: () => void;
  onLiveWidthChange?: (width: number) => void;
  onCancelResize?: () => void;
}

const ColumnHeaderMenu: React.FC<IProps> = ({
  title,
  columnWidth,
  menuOptions,
  onSortAsc,
  onSortDesc,
  onMoveLeft,
  onMoveRight,
  onMoveFirst,
  onMoveLast,
  onHide,
  onResize,
  onStartResize,
  onLiveWidthChange,
  onCancelResize,
}) => {
  const resizePopover = useToggleVisibility();

  const handleOpenResize = () => {
    onStartResize?.();
    resizePopover.show();
  };

  const handleResizeSave = (width: number) => {
    onResize?.(width);
    resizePopover.hide();
  };

  const handleResizeCancel = () => {
    onCancelResize?.();
    resizePopover.hide();
  };

  const menuItems = useMemo((): MenuProps['items'] => {
    const items: MenuProps['items'] = [];

    // if (!menuOptions?.disableSort) {
    //   items.push(
    //     {
    //       key: 'sort-asc',
    //       label: 'Sort Ascending',
    //       icon: <SortAscendingOutlined />,
    //       onClick: () => onSortAsc?.(),
    //     },
    //     {
    //       key: 'sort-desc',
    //       label: 'Sort Descending',
    //       icon: <SortDescendingOutlined />,
    //       onClick: () => onSortDesc?.(),
    //     }
    //   );
    // }

    if (!menuOptions?.disableMove) {
      if (items.length > 0) {
        items.push({ type: 'divider' });
      }

      items.push(
        {
          key: 'move-left',
          label: 'Move Left',
          icon: <ArrowLeftOutlined />,
          onClick: () => onMoveLeft?.(),
        },
        {
          key: 'move-right',
          label: 'Move Right',
          icon: <ArrowRightOutlined />,
          onClick: () => onMoveRight?.(),
        },
        {
          key: 'move-first',
          label: 'Move to First',
          icon: <VerticalAlignTopOutlined />,
          onClick: () => onMoveFirst?.(),
        },
        {
          key: 'move-last',
          label: 'Move to Last',
          icon: <VerticalAlignBottomOutlined />,
          onClick: () => onMoveLast?.(),
        }
      );
    }

    if (!menuOptions?.disableHide) {
      if (items.length > 0) {
        items.push({ type: 'divider' });
      }

      items.push({
        key: 'hide',
        label: 'Hide Column',
        icon: <EyeInvisibleOutlined />,
        onClick: () => onHide?.(),
      });
    }

    if (!menuOptions?.disableResize) {
      if (items.length > 0) {
        items.push({ type: 'divider' });
      }

      items.push({
        key: 'resize',
        label: 'Resize Column',
        icon: <ColumnWidthOutlined />,
        onClick: handleOpenResize,
      });
    }

    return items;
  }, [
    menuOptions?.disableSort,
    menuOptions?.disableMove,
    menuOptions?.disableHide,
    menuOptions?.disableResize,
    onSortAsc,
    onSortDesc,
    onMoveLeft,
    onMoveRight,
    onMoveFirst,
    onMoveLast,
    onHide,
  ]);

  if (!menuItems || menuItems.length === 0) {
    return <>{title}</>;
  }

  return (
    <HeaderWrapper>
      <span>{title}</span>
      <Dropdown
        trigger={['click']}
        menu={{ items: menuItems }}
        placement="bottomRight"
      >
        <MenuIcon
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
          }}
          aria-label="Column options"
        />
      </Dropdown>
      {!menuOptions?.disableResize && (
        <ColumnResizePopover
          open={resizePopover.isVisible}
          title={String(title ?? 'Column')}
          currentWidth={columnWidth ?? DEFAULT_COLUMN_WIDTH}
          onSave={handleResizeSave}
          onCancel={handleResizeCancel}
          onLiveWidthChange={onLiveWidthChange}
        >
          <PopoverAnchor />
        </ColumnResizePopover>
      )}
    </HeaderWrapper>
  );
};

const HeaderWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm};
  width: 100%;

  &:hover ${() => MenuIcon} {
    opacity: 1;
  }
`;

const PopoverAnchor = styled.span`
  position: absolute;
  right: 0;
  top: 50%;
  width: 1px;
  height: 1px;
  pointer-events: none;
`;

const MenuIcon = styled(EllipsisOutlined)`
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s ease;
`;

export default ColumnHeaderMenu;
